import { api, bff } from '@/lib/api/client';

export type DocumentStatus = 'pending' | 'uploaded' | 'indexing' | 'indexed' | 'failed';
export type DocumentCollection = 'lease_documents' | 'lease_reports';

export interface LibraryDocument {
  id: string;
  name: string;
  originalFileName: string;
  type: string;
  description?: string;
  status: DocumentStatus;
  collection: DocumentCollection;
  propertyKey: string;
  /** The tenant; absent = the whole property. */
  partyId?: string;
  party?: Tenant;
  revision: number;
  sizeBytes?: number;
  indexError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Property {
  key: string;
  name: string;
}

export interface Tenant {
  id: string;
  key: string;
  canonicalName: string;
  status: 'current' | 'future' | 'former' | 'non_tenant_occupant';
}

interface UploadTicket {
  document: LibraryDocument;
  upload: { method: 'PUT'; url: string; headers: Record<string, string> };
}

/** What Simon's search (Vertex AI Search) can index. api-core-go dist/library/library_upload.py keeps the same list. */
export const SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.pptx', '.xlsx', '.xlsm', '.html', '.htm', '.txt', '.json'];

export function isSupportedFile(name: string): boolean {
  const dot = name.lastIndexOf('.');
  return dot >= 0 && SUPPORTED_EXTENSIONS.includes(name.slice(dot).toLowerCase());
}

export const UNSUPPORTED_HINT = `Simon can't search that file type. Use ${SUPPORTED_EXTENSIONS.join(', ')}; save emails as PDF first.`;

export const COLLECTIONS: { key: DocumentCollection; label: string }[] = [
  { key: 'lease_documents', label: 'Lease documents' },
  { key: 'lease_reports', label: 'Lease reports' },
];

const PAGE = 200; // sreg-api's page limit

/** Every document in a collection (all pages), optionally for some properties only. */
export async function listDocuments(
  f: { collection: DocumentCollection; q?: string; propertyKeys?: string[] },
  signal?: AbortSignal,
): Promise<LibraryDocument[]> {
  const out: LibraryDocument[] = [];
  for (let offset = 0; ; offset += PAGE) {
    const page = await api.get<LibraryDocument[]>(
      '/documents',
      { collection: f.collection, q: f.q, propertyKey: f.propertyKeys?.join(','), limit: PAGE, offset },
      signal,
    );
    out.push(...page);
    if (page.length < PAGE) return out;
  }
}

export function listProperties(signal?: AbortSignal): Promise<Property[]> {
  return api.get<Property[]>('/properties', undefined, signal);
}

export function listTenants(propertyKey: string, signal?: AbortSignal): Promise<Tenant[]> {
  return api.get<Tenant[]>(`/properties/${encodeURIComponent(propertyKey)}/tenants`, undefined, signal);
}

/** The file goes straight to GCS on a short-lived signed URL; nothing proxies the bytes. */
async function putToSignedUrl(ticket: UploadTicket, file: File): Promise<void> {
  const r = await fetch(ticket.upload.url, { method: 'PUT', headers: ticket.upload.headers, body: file });
  if (!r.ok) throw new Error(`Upload to storage failed (${r.status})`);
}

const contentTypeOf = (file: File) => file.type || 'application/octet-stream';

/** start -> PUT -> complete (indexes). Resolves with the final document (indexed or failed). */
export async function uploadDocument(
  file: File,
  meta: { name?: string; collection: DocumentCollection; propertyKey: string; partyId?: string },
  onStep?: (step: 'uploading' | 'indexing') => void,
): Promise<LibraryDocument> {
  const ticket = await bff.post<UploadTicket>('/documents', {
    name: meta.name?.trim() || file.name,
    originalFileName: file.name,
    contentType: contentTypeOf(file),
    sizeBytes: file.size,
    collection: meta.collection,
    propertyKey: meta.propertyKey,
    partyId: meta.partyId,
  });
  onStep?.('uploading');
  await putToSignedUrl(ticket, file);
  onStep?.('indexing');
  return bff.post<LibraryDocument>(`/documents/${ticket.document.id}/complete`);
}

export async function replaceDocument(id: string, file: File, onStep?: (step: 'uploading' | 'indexing') => void) {
  const ticket = await bff.post<UploadTicket>(`/documents/${id}/replace`, {
    originalFileName: file.name,
    contentType: contentTypeOf(file),
    sizeBytes: file.size,
  });
  onStep?.('uploading');
  await putToSignedUrl(ticket, file);
  onStep?.('indexing');
  return bff.post<LibraryDocument>(`/documents/${id}/complete`);
}

/** Retry indexing a failed document (the file is already stored). */
export function reindexDocument(id: string): Promise<LibraryDocument> {
  return bff.post<LibraryDocument>(`/documents/${id}/complete`);
}

export async function downloadUrl(id: string): Promise<string> {
  return (await bff.get<{ url: string }>(`/documents/${id}/download`)).url;
}

export function deleteDocument(id: string): Promise<void> {
  return bff.del<void>(`/documents/${id}`);
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Existing documents in a collection with this exact file name (case-insensitive). */
export async function findByFileName(collection: DocumentCollection, fileName: string): Promise<LibraryDocument[]> {
  const rows = await listDocuments({ collection, q: fileName });
  return rows.filter((d) => sameName(d.originalFileName, fileName));
}

/** Replacing with a differently named file deserves a second look. */
export function fileNameChanged(doc: Pick<LibraryDocument, 'originalFileName'>, file: File): boolean {
  return !sameName(doc.originalFileName, file.name);
}
